/**
 * Saved View editor (design/reboot/views.js `open()` ported; web.md §4): name and icon (no colour —
 * fix 8), a recursive ALL/ANY tree with multi-value pickers, a live count with an expandable preview
 * from `views.preview` (the same compiled query as the sidebar count and the list), and Create / Save /
 * Delete (with Undo). Same markup and classes as the prototype (`vb-*`).
 */

import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toDigesterError, type MessageRow, type View, type ViewId } from '@digester/core';
import { DialogFrame, type DialogProps } from '../app/dialogs.js';
import { isPlanError, reportError } from '../app/errors.js';
import { useViews } from '../app/hooks.js';
import { showToast } from '../app/Toast.js';
import { useEngine } from '../engine-client/EngineProvider.js';
import { Icon } from '../icons/Icon.js';
import { IconField } from '../icons/IconField.js';
import { FolderIcon } from '../ui/FolderIcon.js';
import { SOURCE_KIND_LABELS, sourceKindIcon } from '../ui/source-icons.js';
import { useLookups, type Lookups } from './data.js';
import {
  VIEW_FIELDS, SET_FIELDS, fromQuery, isComplete, mapNode, newCondition, newGroup, operatorsFor, removeNode, toQuery,
  type EditCondition, type EditGroup, type EditNode, type ViewField,
} from './view-tree.js';

export interface ViewBuilderProps {
  /** Edit this view; absent = create. */
  viewId?: ViewId;
}

const PREVIEW_ROWS = 20;
const PREVIEW_DEBOUNCE_MS = 180;

interface Option {
  id: string;
  name: string;
  icon: string;
}

function optionsFor(field: ViewField, lookups: Lookups): Option[] {
  switch (field) {
    case 'folder':
      return lookups.folders.map((f) => ({ id: f.id, name: f.name, icon: f.icon }));
    case 'source':
      return lookups.sources.filter((s) => s.archivedAt === null).map((s) => ({ id: s.id, name: s.name, icon: sourceKindIcon(s.kind) }));
    case 'topic':
      return lookups.topics.filter((t) => t.archivedAt === null).map((t) => ({ id: t.id, name: t.name, icon: t.icon }));
    case 'tag':
      return [...lookups.tags].sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ id: t.id, name: t.name, icon: t.icon || 'tag' }));
    case 'source-type':
      return (Object.keys(SOURCE_KIND_LABELS) as Array<keyof typeof SOURCE_KIND_LABELS>).map((k) => ({ id: k, name: SOURCE_KIND_LABELS[k], icon: sourceKindIcon(k) }));
    default:
      return [];
  }
}

export function ViewBuilder({ close, dialogId, viewId }: DialogProps & ViewBuilderProps) {
  const views = useViews();
  const existing = viewId ? views.find((v) => v.id === viewId) : undefined;
  if (viewId && !existing) return null;
  return <ViewBuilderForm close={close} dialogId={dialogId} view={existing ?? null} />;
}

function ViewBuilderForm({ close, dialogId, view }: DialogProps & { view: View | null }) {
  const engine = useEngine();
  const navigate = useNavigate();
  const lookups = useLookups();
  const [name, setName] = useState(view?.name ?? '');
  const [icon, setIcon] = useState(view?.icon ?? 'folder');
  const [root, setRoot] = useState<EditGroup>(() => {
    if (view) return fromQuery(view.query) as EditGroup;
    return newGroup([newCondition()]);
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<{ total: number; rows: MessageRow[] } | null>(null);
  const [validation, setValidation] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const focusKey = useRef<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const query = useMemo(() => toQuery(root), [root]);

  // Live count + preview from the engine (never rejects on incomplete trees).
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      engine.views
        .preview(query, PREVIEW_ROWS)
        .then((r) => {
          if (!cancelled) setPreview(r);
        })
        .catch((error: unknown) => {
          if (!cancelled) setValidation(toDigesterError(error).message);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [engine, query]);

  useEffect(() => {
    if (!focusKey.current) return;
    formRef.current?.querySelector<HTMLSelectElement>(`[data-vb-node="${focusKey.current}"] select`)?.focus();
    focusKey.current = null;
  }, [root]);

  const update = (key: string, fn: (n: EditNode) => EditNode): void => {
    setValidation(null);
    setRoot((r) => mapNode(r, key, fn));
  };
  const add = (groupKey: string, node: EditNode): void => {
    focusKey.current = node.key;
    update(groupKey, (g) => (g.type === 'group' ? { ...g, children: [...g.children, node] } : g));
  };
  const remove = (key: string): void => {
    setValidation(null);
    setRoot((r) => removeNode(r, key));
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (!isComplete(root)) {
      setValidation('Complete each condition or remove it before saving.');
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) {
      nameRef.current?.setCustomValidity('Enter a view name.');
      nameRef.current?.reportValidity();
      return;
    }
    setBusy(true);
    try {
      const saved = view ? await engine.views.update(view.id, { name: trimmed, icon, query }) : await engine.views.create({ name: trimmed, icon, query });
      close();
      navigate(`/v/${encodeURIComponent(saved.id)}`);
      showToast('View saved');
    } catch (error) {
      if (isPlanError(error)) reportError(error);
      else setValidation(toDigesterError(error).message);
      setBusy(false);
    }
  };

  const deleteView = async (): Promise<void> => {
    if (!view) return;
    setBusy(true);
    try {
      const result = await engine.views.delete(view.id);
      close();
      navigate('/f/inbox');
      showToast('View deleted', result.undoId);
    } catch (error) {
      reportError(error);
      setBusy(false);
    }
  };

  const count = preview?.total;
  return (
    <DialogFrame title={view ? 'Edit view' : 'Create view'} className="view-builder-dialog" onClose={close} dialogId={dialogId}>
      <form id="view-builder-form" ref={formRef} noValidate={false} onSubmit={(e) => void submit(e)}>
        <div className="vb-identity">
          <label className="field">
            <span>View name</span>
            <input
              ref={nameRef}
              name="name"
              required
              maxLength={80}
              placeholder="e.g. Research to read"
              autoComplete="off"
              value={name}
              onChange={(e) => {
                e.target.setCustomValidity('');
                setName(e.target.value);
              }}
            />
          </label>
          <IconField label="Icon" name="icon" value={icon} onChange={setIcon} />
        </div>
        <GroupView node={root} depth={0} lookups={lookups} update={update} add={add} remove={remove} />
        <div className="vb-preview">
          <button className="vb-match-toggle" type="button" aria-expanded={previewOpen} onClick={() => setPreviewOpen((o) => !o)}>
            <span>
              <Icon name="eye" />
              <strong id="vb-match-count" data-count={count ?? ''}>
                {count === undefined ? 'Counting…' : `${count.toLocaleString('en')} ${count === 1 ? 'message matches' : 'messages match'}`}
              </strong>
            </span>
            <span>
              {previewOpen ? 'Hide' : 'Preview'} <Icon name="chevron-down" />
            </span>
          </button>
          <div id="vb-matches" hidden={!previewOpen}>
            {preview && preview.rows.length ? (
              preview.rows.map((m) => (
                <div className="vb-preview-message" key={m.id}>
                  <span>
                    <FolderIcon folder={lookups.folder(m.folderId)} />
                  </span>
                  <div>
                    <strong>{m.title ?? m.excerpt ?? 'Untitled message'}</strong>
                    <small>
                      {lookups.source(m.sourceId)?.name ?? ''} · {lookups.folder(m.folderId)?.name ?? ''}
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <p className="vb-empty">No messages match these conditions.</p>
            )}
            {preview && preview.total > preview.rows.length ? <p className="vb-empty">and {(preview.total - preview.rows.length).toLocaleString('en')} more</p> : null}
          </div>
        </div>
        <p className="vb-validation" role="alert" hidden={!validation}>
          {validation}
        </p>
        <div className="dialog-footer vb-footer">
          {view ? (
            <button type="button" className="vb-delete" disabled={busy} onClick={() => void deleteView()}>
              <Icon name="trash-2" /> Delete view
            </button>
          ) : null}
          <span className="vb-footer-spacer" />
          <button type="button" className="secondary-button" onClick={close}>
            Cancel
          </button>
          <button className="primary-button" type="submit" disabled={busy}>
            {view ? 'Save view' : 'Create view'}
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}

interface NodeOps {
  lookups: Lookups;
  update(key: string, fn: (n: EditNode) => EditNode): void;
  add(groupKey: string, node: EditNode): void;
  remove(key: string): void;
}

function Choice({ value, options, label, onChange, part }: { value: string; options: ReadonlyArray<readonly [string, string]>; label: string; onChange(v: string): void; part: string }) {
  return (
    <select data-vb-part={part} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([id, text]) => (
        <option key={id} value={id}>
          {text}
        </option>
      ))}
    </select>
  );
}

function GroupView({ node, depth, ...ops }: NodeOps & { node: EditGroup; depth: number }) {
  const { update, add, remove } = ops;
  return (
    <section className={`vb-group ${depth ? 'vb-nested' : 'vb-root'}${depth > 2 ? ' vb-deep' : ''}`} data-vb-node={node.key} aria-label={depth ? 'Nested condition group' : 'View conditions'}>
      <div className="vb-group-heading">
        <span>{depth ? <Icon name="list-filter" /> : 'Match'}</span>
        <Choice
          part="join"
          label="Match all or any conditions"
          value={node.operator}
          options={[
            ['all', 'All'],
            ['any', 'Any'],
          ]}
          onChange={(v) => update(node.key, (n) => (n.type === 'group' ? { ...n, operator: v === 'any' ? 'any' : 'all' } : n))}
        />
        <span>of the following</span>
        {depth ? (
          <button className="vb-remove" type="button" aria-label="Remove group" title="Remove group" onClick={() => remove(node.key)}>
            <Icon name="x" />
          </button>
        ) : null}
      </div>
      <div className="vb-group-children">
        {node.children.map((child, index) => (
          <Fragment key={child.key}>
            {index ? (
              <div className="vb-conjunction" aria-hidden="true">
                {node.operator === 'all' ? 'AND' : 'OR'}
              </div>
            ) : null}
            {child.type === 'group' ? <GroupView node={child} depth={depth + 1} {...ops} /> : <ConditionView node={child} {...ops} />}
          </Fragment>
        ))}
        {!node.children.length ? <p className="vb-empty">{depth ? 'Add a condition to complete this group.' : 'All messages. Add conditions to narrow this view.'}</p> : null}
      </div>
      <div className="vb-group-actions">
        <button type="button" onClick={() => add(node.key, newCondition())}>
          <Icon name="plus" /> Add condition
        </button>
        <button type="button" onClick={() => add(node.key, newGroup([newCondition('title')]))}>
          <Icon name="list-filter" /> Add group
        </button>
      </div>
    </section>
  );
}

function ConditionView({ node, lookups, update, remove }: NodeOps & { node: EditCondition }) {
  const set = (patch: Partial<EditCondition>): void => update(node.key, (n) => (n.type === 'condition' ? { ...n, ...patch } : n));
  return (
    <div className="vb-condition" data-vb-node={node.key}>
      <Choice part="field" label="Condition field" value={node.field} options={VIEW_FIELDS} onChange={(v) => update(node.key, () => ({ ...newCondition(v as ViewField), key: node.key }))} />
      <Choice part="operator" label="Condition comparison" value={node.operator} options={operatorsFor(node.field)} onChange={(operator) => set({ operator })} />
      <div className="vb-value">
        <ValueControl node={node} lookups={lookups} set={set} />
      </div>
      <button className="vb-remove" type="button" aria-label="Remove condition" title="Remove condition" onClick={() => remove(node.key)}>
        <Icon name="x" />
      </button>
    </div>
  );
}

function ValueControl({ node, lookups, set }: { node: EditCondition; lookups: Lookups; set(patch: Partial<EditCondition>): void }) {
  if (SET_FIELDS.has(node.field)) return <SetPicker node={node} lookups={lookups} set={set} />;
  if (node.field === 'read')
    return (
      <Choice
        part="value"
        label="Read status"
        value={node.value}
        options={[
          ['unread', 'Unread'],
          ['read', 'Read'],
        ]}
        onChange={(value) => set({ value })}
      />
    );
  if (node.field === 'image')
    return (
      <Choice
        part="value"
        label="Image presence"
        value={node.value}
        options={[
          ['yes', 'Has an image'],
          ['no', 'No image'],
        ]}
        onChange={(value) => set({ value })}
      />
    );
  if (node.field === 'topic-score')
    return (
      <div className="vb-score">
        <Choice part="topic" label="Topic to score" value={node.topic} options={[['', 'Any scored topic'], ...lookups.topics.filter((t) => t.archivedAt === null).map((t) => [t.id, t.name] as const)]} onChange={(topic) => set({ topic })} />
        <input type="number" data-vb-part="value" min={0} max={100} required aria-label="Relevance score out of 100" value={node.value} onChange={(e) => set({ value: e.target.value })} />
        {lookups.models.length > 1 || node.model ? (
          <Choice part="model" label="Scored by" value={node.model} options={[['', 'Shown scores'], ...lookups.models.map((m) => [m.id as string, lookups.modelShort(m.id)] as const)]} onChange={(model) => set({ model })} />
        ) : null}
      </div>
    );
  if (node.field === 'hours')
    return (
      <div className="vb-duration">
        <input type="number" data-vb-part="value" min={0} step="any" required aria-label="Time in folder" value={node.value} onChange={(e) => set({ value: e.target.value })} />
        <Choice
          part="unit"
          label="Time unit"
          value={node.unit}
          options={[
            ['hours', 'hours'],
            ['days', 'days'],
          ]}
          onChange={(unit) => set({ unit: unit === 'days' ? 'days' : 'hours' })}
        />
      </div>
    );
  return <input type="text" data-vb-part="value" placeholder="Enter text…" aria-label={`${node.field} text`} required value={node.value} onChange={(e) => set({ value: e.target.value })} />;
}

function SetPicker({ node, lookups, set }: { node: EditCondition; lookups: Lookups; set(patch: Partial<EditCondition>): void }) {
  const engine = useEngine();
  const [search, setSearch] = useState('');
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const options = optionsFor(node.field, lookups);
  for (const id of node.values) if (!options.some((o) => o.id === id)) options.push({ id, name: id, icon: node.field === 'tag' ? 'tag' : 'folder' });
  const text = search.trim().toLowerCase();
  const visible = options.filter((o) => o.name.toLowerCase().includes(text));
  const selected = node.values.map((id) => options.find((o) => o.id === id) ?? { id, name: id, icon: 'folder' });
  const noun = node.field === 'source-type' ? 'types' : `${node.field}s`;

  const toggle = (id: string, on: boolean): void => set({ values: on ? [...new Set([...node.values, id])] : node.values.filter((v) => v !== id) });
  const addTag = async (): Promise<void> => {
    const name = search.trim();
    if (!name) return;
    try {
      const tag = await engine.tags.create({ name, icon: 'tag' });
      set({ values: [...new Set([...node.values, tag.id])] });
      setSearch('');
    } catch (error) {
      reportError(error);
    }
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (node.field === 'tag') void addTag();
    }
  };

  return (
    <details className="vb-picker" data-vb-picker={node.key} ref={detailsRef}>
      <summary aria-label={`Choose ${node.field} values`}>
        {selected.length ? (
          <span className="vb-selection-items">
            {selected.slice(0, 2).map((o) => (
              <span key={o.id}>
                <Icon name={o.icon} />
                {o.name}
              </span>
            ))}
            {selected.length > 2 ? <b>+{selected.length - 2}</b> : null}
          </span>
        ) : (
          <span className="vb-placeholder">Choose {noun}…</span>
        )}
        <Icon name="chevron-down" />
      </summary>
      <div className="vb-picker-menu">
        <label className="vb-picker-search">
          <Icon name="search" />
          <input type="search" data-vb-search="" aria-label={`Search ${node.field} values`} placeholder={`Search${node.field === 'tag' ? ' or add a tag' : ''}…`} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onKey} />
        </label>
        <div className="vb-picker-options">
          {visible.map((o) => (
            <label key={o.id} data-vb-option={o.name.toLowerCase()}>
              <input type="checkbox" data-vb-part="set" value={o.id} checked={node.values.includes(o.id)} onChange={(e) => toggle(o.id, e.target.checked)} />
              <span>
                <Icon name={o.icon} />
              </span>
              <span>{o.name}</span>
            </label>
          ))}
        </div>
        {node.field === 'tag' ? (
          <button type="button" className="vb-add-tag" onClick={() => void addTag()}>
            <Icon name="plus" /> Add typed tag
          </button>
        ) : null}
        {!visible.length && node.field !== 'tag' ? <p className="vb-picker-empty">No matching items</p> : null}
      </div>
    </details>
  );
}
